import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Check } from 'lucide-react';
import apiClient from '../services/api';
import '../styles/MasterForms.css';

const Countries = () => {
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      
      const sessionData = JSON.parse(sessionStr);
      // Handle both direct sessionData and nested sessionData.session (same as DashboardHome)
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();
  const companyId = session?.companyid;
  const yearId = session?.yearid;

  const [countries, setCountries] = useState([]);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    countryid: null,
    countrycode: '',
    countryname: '',
    isactive: true
  });
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    filterCountries();
  }, [searchTerm, countries]);

  const fetchCountries = async () => {
    setLoading(true);
    try {
          // ✅ Use session company ID from context
          const response = await apiClient.get('/Countries.php', {
            params: { 
            }
          });
     
       if (response.data.success) {
        setCountries(response.data.data || []);
      }  
    } catch (error) {
      console.error('Error fetching countries:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } 
    } finally {
      setLoading(false);
    }
  };

  const filterCountries = () => {
    if (!searchTerm.trim()) {
      setFilteredCountries(countries);
    } else {
      const filtered = countries.filter(country =>
        country.countryname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        country.countrycode.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCountries(filtered);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.countrycode.trim()) {
      newErrors.countrycode = 'Country code is required';
    } else if (formData.countrycode.length > 10) {
      newErrors.countrycode = 'Country code must be 10 characters or less';
    }
    
    if (!formData.countryname.trim()) {
      newErrors.countryname = 'Country name is required';
    } else if (formData.countryname.length > 100) {
      newErrors.countryname = 'Country name must be 100 characters or less';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitLoading(true);
    try {
      // ✅ Use apiClient with session-based company ID
      const method = editMode ? 'put' : 'post';
      const response = await apiClient[method]('/Countries.php', {
        ...formData,   // ✅ Ensure session company ID is used
        statushide: formData.isactive ? 1 : 0
      });
      
      const data = response.data;    
      
      if (data.success) {
        alert(editMode ? 'Country updated successfully!' : 'Country created successfully!');
        setShowModal(false);
        resetForm();
        fetchCountries();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error saving country:', error);
      alert('Error saving country. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (country) => {
    setFormData({
      countryid: country.countryid,
      countrycode: country.countrycode,
      countryname: country.countryname,
      isactive: country.isactive === 1
    });
    setEditMode(true);
    setShowModal(true);
    setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this country?')) {
      return;
    }
    
    try {
      const response = await fetch('/api/Countries.php', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ countryid: id })
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('Country deleted successfully!');
        fetchCountries();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting country:', error);
      alert('Error deleting country. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      countryid: null,
      countrycode: '',
      countryname: '',
      isactive: true
    });
    setEditMode(false);
    setErrors({});
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  return (
    <div className="master-container">
      <div className="master-header">
        <div className="header-left">
          <h2>Countries Master</h2>
          <p className="subtitle">Manage country information</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={20} />
          Add Country
        </button>
      </div>

      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by country name or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        <div className="toolbar-info">
          Total: {filteredCountries.length} {filteredCountries.length === 1 ? 'country' : 'countries'}
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading countries...</div>
      ) : filteredCountries.length === 0 ? (
        <div className="empty-state">
          <p>No countries found</p>
          {searchTerm && <button onClick={() => setSearchTerm('')}>Clear search</button>}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Country Name</th>
                <th>Status</th>
                <th>Created At</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCountries.map((country) => (
                <tr key={country.countryid}>
                  <td className="font-mono">{country.countrycode}</td>
                  <td className="font-semibold">{country.countryname}</td>
                  <td>
                    <span className={`status-badge ${country.isactive ? 'active' : 'inactive'}`}>
                      {country.isactive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{new Date(country.createdat).toLocaleDateString()}</td>
                  <td className="actions-column">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(country)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(country.countryid)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Country' : 'Add New Country'}</h3>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="countrycode">
                    Country Code <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="countrycode"
                    value={formData.countrycode}
                    onChange={(e) => setFormData({ ...formData, countrycode: e.target.value.toUpperCase() })}
                    maxLength="10"
                    placeholder="e.g., SA, AE, IN"
                    className={errors.countrycode ? 'error' : ''}
                  />
                  {errors.countrycode && (
                    <span className="error-message">{errors.countrycode}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="countryname">
                    Country Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="countryname"
                    value={formData.countryname}
                    onChange={(e) => setFormData({ ...formData, countryname: e.target.value })}
                    maxLength="100"
                    placeholder="e.g., Saudi Arabia"
                    className={errors.countryname ? 'error' : ''}
                  />
                  {errors.countryname && (
                    <span className="error-message">{errors.countryname}</span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isactive}
                    onChange={(e) => setFormData({ ...formData, isactive: e.target.checked })}
                  />
                  <span>Active</span>
                </label>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={submitLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitLoading}
                >
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Countries;